/**
 * EspaceEleve — Hub unifié de l'espace élève connecté.
 *
 * Refonte juin 2026 : remplace l'éparpillement entre /profil (carnet) et
 * /parcours (lectures) par une expérience à 3 onglets sticky :
 *
 *   • Mon journal   → Carnet de bord du motard (composant Profil)
 *   • Mes lectures  → Suivi des dossiers ETM lus / favoris (MonJournal)
 *   • Mon profil    → Identité, paramètres, déconnexion
 *
 * URLs préservées :
 *   /profil    → onglet "journal"
 *   /parcours  → onglet "lectures"
 *   /compte    → onglet "profil"
 *
 * Deep-link via querystring `?tab=...` géré par StickyMagazineTabs.
 */
import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Map as MapIcon, BookOpenCheck, UserCog, LogOut, Mail, Calendar, ShieldCheck,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useApp } from '../contexts/AppContext';
import StickyMagazineTabs from '../components/StickyMagazineTabs';
import Profil from './Profil';
import MonJournal from './MonJournal';
import useSeo from '../hooks/useSeo';

// ─── Panneau "Mon profil" (3e onglet, nouveau) ───────────────────────
function ProfilPanel() {
  const { user, logout } = useApp();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      navigate('/', { replace: true });
    }
  };

  if (!user) {
    return (
      <Card data-testid="panel-profil-anonymous">
        <CardContent className="p-6 text-center">
          <p className="text-sm text-muted-foreground mb-3">
            Vous n'êtes pas connecté.
          </p>
          <Button onClick={() => navigate('/login')} data-testid="profil-login-cta">
            Se connecter
          </Button>
        </CardContent>
      </Card>
    );
  }

  const initials = (user.name || user.email || '?')
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <section className="space-y-5" data-testid="panel-profil-content">
      {/* Identité */}
      <Card>
        <CardContent className="p-5 sm:p-6">
          <div className="flex items-start gap-4">
            <div
              className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center text-white font-black text-xl sm:text-2xl flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #cf2031, #7c3aed)' }}
              aria-hidden="true"
            >
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">
                Bonjour
              </div>
              <h2
                className="text-xl sm:text-2xl font-black tracking-tight leading-tight truncate"
                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                data-testid="profil-display-name"
              >
                {user.name || user.email || 'Lecteur Code 2 Roues'}
              </h2>
              <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <Mail className="w-3 h-3" /> {user.email}
                </span>
                {user.created_at && (
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> Membre depuis{' '}
                    {new Date(user.created_at).toLocaleDateString('fr-FR', {
                      year: 'numeric',
                      month: 'long',
                    })}
                  </span>
                )}
                {user.role && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted text-foreground/80 capitalize">
                    <ShieldCheck className="w-3 h-3" /> {user.role}
                  </span>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Paramètres rapides */}
      <Card>
        <CardContent className="p-5 sm:p-6">
          <h3 className="text-sm font-black uppercase tracking-wider text-muted-foreground mb-3">
            Paramètres
          </h3>
          <ul className="divide-y divide-border">
            <li className="py-3 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-sm font-bold">Préférences de lecture</div>
                <div className="text-xs text-muted-foreground">
                  Thème (clair / sombre) — réglable depuis la barre supérieure
                </div>
              </div>
            </li>
            <li className="py-3 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-sm font-bold">Newsletter Code 2 Roues</div>
                <div className="text-xs text-muted-foreground">
                  Nouveaux dossiers + alertes sécurité routière
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate('/?newsletter=1')}
                data-testid="profil-newsletter-cta"
              >
                Gérer
              </Button>
            </li>
            <li className="py-3 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-sm font-bold">Données personnelles</div>
                <div className="text-xs text-muted-foreground">
                  Politique de confidentialité &amp; suppression de compte
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate('/mentions-legales')}
                data-testid="profil-legal-cta"
              >
                Voir
              </Button>
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* Déconnexion */}
      <div className="flex justify-center pt-2">
        <Button
          variant="outline"
          onClick={handleLogout}
          className="gap-2"
          data-testid="profil-logout-button"
        >
          <LogOut className="w-4 h-4" /> Se déconnecter
        </Button>
      </div>
    </section>
  );
}

// ─── Page principale ─────────────────────────────────────────────────

export default function EspaceEleve() {
  const location = useLocation();

  // Mapping path → tab id (gère /profil, /parcours, /compte sans casser le SEO)
  const pathToTab = {
    '/profil': 'journal',
    '/parcours': 'lectures',
    '/compte': 'profil',
  };

  useSeo({
    title: 'Mon espace · Code 2 Roues',
    description:
      "Votre espace personnel Code 2 Roues : carnet de bord, suivi de lecture, paramètres. Sans score, sans pression — votre voyage vers le permis moto à votre rythme.",
    path: location.pathname,
  });

  const tabs = [
    {
      id: 'journal',
      label: 'Mon carnet',
      kicker: 'Carnet de bord',
      icon: MapIcon,
      content: <Profil />,
    },
    {
      id: 'lectures',
      label: 'Mes lectures',
      kicker: '9 dossiers magazine',
      icon: BookOpenCheck,
      content: <MonJournal />,
    },
    {
      id: 'profil',
      label: 'Mon profil',
      kicker: 'Compte',
      icon: UserCog,
      content: <ProfilPanel />,
    },
  ];

  // Si on arrive sur /parcours ou /compte sans ?tab=, ouvrir l'onglet correspondant.
  // (Géré naturellement par StickyMagazineTabs via storageKey + URL, mais on
  // sur-rectifie via une querystring synthétique pour assurer la cohérence.)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (!url.searchParams.get('tab')) {
      const t = pathToTab[location.pathname];
      if (t) {
        url.searchParams.set('tab', t);
        window.history.replaceState({}, '', url.toString());
      }
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-2" data-testid="espace-eleve-page">
      <StickyMagazineTabs
        tabs={tabs}
        storageKey="student-hub"
        stickyTop="0px"
      />
    </div>
  );
}
